
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, TextField, Button, Snackbar, Alert, Paper, FormControlLabel, Switch, Avatar } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { getAis, updateCollectionSettings, toggleCollectionPublic } from '../services/api';
import config from '../config';
import CurtainManagement from '../components/CurtainManagement';

// Removed global $crisp interface - no longer using Crisp widget

interface ChatProps {}

interface Collection {
  _id: string;
  version: string;
  instruction?: string;
  temperature?: number;
  qaPairsLimit?: number;
  debugOutline?: boolean;
  color?: string;
  chatbotTitle?: string;
  assistantName?: string;
  chatbotIcon?: string;
  isPublic?: boolean;
}

interface AI {
  id: string;
  title: string;
  domain: string;
  description: string;
  collections: Collection[];
}

const ChatWidget: React.FC<ChatProps> = () => {
  const { aiId } = useParams<{ aiId: string }>();
  const [searchParams] = useSearchParams();
  const collectionId = searchParams.get('collectionId');
  const navigate = useNavigate();
  const [ai, setAi] = useState<AI | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [demoCopied, setDemoCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [editingChatbotTitle, setEditingChatbotTitle] = useState('');
  const [editingAssistantName, setEditingAssistantName] = useState('');
  const [editingColor, setEditingColor] = useState('#2196f3');
  const [editingDebugOutline, setEditingDebugOutline] = useState(false);
  const [editingChatbotIcon, setEditingChatbotIcon] = useState('');
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [showCurtainManagement, setShowCurtainManagement] = useState(false);

  // Construct the webhook URL
  // For production, the backend API is accessible at https://aitrainee.net/api
  // For development, use localhost
  let baseUrl = window.location.origin; // Gets the protocol, hostname, and port
  
  // Use config for backend URL instead of hardcoded mapping
  baseUrl = config.apiBaseUrl;
  // For other environments, use the origin as-is
  const webhookUrl = `${baseUrl}/api/webhook/crisp/ai/${aiId}/collection/${collectionId}`;
  
  // Construct the demo URL 
  const demoUrl = `${window.location.origin}/demo/${aiId}?collectionId=${collectionId}`;
  
  // Construct the embed code using public widget route (bypasses auth issues)
  const embedCode = `<script 
  src="${baseUrl}/api/widget/widget.js" 
  data-ai-id="${aiId}"
  data-collection-id="${collectionId}"
  data-position="bottom-right"
  data-theme="light">
</script>`;

  useEffect(() => {
    // Fetch AI information
    if (aiId) {
      setLoading(true);
      getAis()
        .then((res) => {
          const foundAi = res.data.find((ai: AI) => ai.id === aiId);
          if (foundAi) {
            setAi(foundAi);
            // Initialize editing values from current collection
            const currentCollection = foundAi.collections?.find((collection: Collection) => collection._id === collectionId);
            if (currentCollection) {
              setEditingChatbotTitle(currentCollection.chatbotTitle || '');
              setEditingAssistantName(currentCollection.assistantName || '');
              setEditingColor(currentCollection.color || '#2196f3');
              setEditingDebugOutline(currentCollection.debugOutline || false);
              setEditingChatbotIcon(currentCollection.chatbotIcon || '');
              setIconPreview(currentCollection.chatbotIcon || null);
            }
          } else {
            setError('AI not found');
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching AI information:', err);
          setError('Error loading AI information');
          setLoading(false);
        });
    }
  }, [aiId, collectionId]);

  // Find the current collection to get its version
  const currentCollection = ai?.collections?.find(collection => collection._id === collectionId);

  // Auto-detect smartview feature and expand curtain management section
  useEffect(() => {
    if (currentCollection?.instruction) {
      const hasSmartview = currentCollection.instruction
        .trim()
        .toLowerCase()
        .includes('smartview');

      if (hasSmartview) {
        setShowCurtainManagement(true);
      }
    }
  }, [currentCollection]);

  // Crisp widget removed - businesses will configure their own Crisp with the webhook URL

  // Function to copy webhook URL to clipboard
  const handleCopyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000); // Hide the alert after 3 seconds
      })
      .catch((err) => {
        console.error('Failed to copy webhook URL: ', err);
      });
  };

  // Function to copy demo URL to clipboard
  const handleCopyDemo = () => {
    navigator.clipboard.writeText(demoUrl)
      .then(() => {
        setDemoCopied(true);
        setTimeout(() => setDemoCopied(false), 3000); // Hide the alert after 3 seconds
      })
      .catch((err) => {
        console.error('Failed to copy demo URL: ', err);
      });
  };

  // Function to copy embed code to clipboard
  const handleCopyEmbed = () => {
    navigator.clipboard.writeText(embedCode)
      .then(() => {
        setEmbedCopied(true);
        setTimeout(() => setEmbedCopied(false), 3000); // Hide the alert after 3 seconds
      })
      .catch((err) => {
        console.error('Failed to copy embed code: ', err);
      });
  };

  // Function to handle icon file upload
  const handleIconUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file must be less than 5MB');
        return;
      }

      // Convert to base64 for preview and storage
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setIconPreview(result);
        setEditingChatbotIcon(result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Function to reset icon to default
  const handleResetIcon = () => {
    setEditingChatbotIcon('');
    setIconPreview(null);
  };

  // Function to save appearance settings
  const handleSaveSettings = async () => {
    if (!aiId || !collectionId) return;

    try {
      await updateCollectionSettings(aiId, collectionId, {
        chatbotTitle: editingChatbotTitle,
        assistantName: editingAssistantName,
        color: editingColor,
        debugOutline: editingDebugOutline,
        chatbotIcon: editingChatbotIcon
      });

      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);

      // Refresh AI data to reflect changes
      const res = await getAis();
      const foundAi = res.data.find((ai: AI) => ai.id === aiId);
      if (foundAi) {
        setAi(foundAi);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  // Function to handle curtain management button click
  const handleCurtainButtonClick = () => {
    setShowCurtainManagement(true);
    // Scroll to curtain section after state update
    setTimeout(() => {
      document.getElementById('curtain-management-section')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  // Reset chat function removed - no longer using Crisp widget

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          AI Integration Options
        </Typography>
        <Button
          variant={currentCollection?.instruction?.trim().toLowerCase().includes('smartview') ? 'contained' : 'outlined'}
          color="secondary"
          startIcon={<PhotoLibraryIcon />}
          onClick={handleCurtainButtonClick}
          sx={{
            bgcolor: currentCollection?.instruction?.trim().toLowerCase().includes('smartview') ? '#9c27b0' : 'transparent',
            borderColor: '#9c27b0',
            color: currentCollection?.instruction?.trim().toLowerCase().includes('smartview') ? 'white' : '#9c27b0',
            '&:hover': {
              bgcolor: currentCollection?.instruction?.trim().toLowerCase().includes('smartview') ? '#7b1fa2' : 'rgba(156, 39, 176, 0.08)'
            }
          }}
        >
          Manage Curtains
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body1">Loading AI information...</Typography>
        </Box>
      ) : error ? (
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      ) : ai ? (
        <Box sx={{ mt: 2, mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">
              {ai.title}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Version: {currentCollection?.version || 'Unknown'}
            </Typography>
            {ai.description && (
              <Typography variant="body1" sx={{ mt: 1 }}>
                {ai.description}
              </Typography>
            )}
          </Box>

          {/* Widget Appearance Settings */}
          <Paper sx={{ p: 3, mt: 4, bgcolor: '#fff3e0', border: '2px solid #ff9800' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#e65100' }}>
              🎨 Widget Appearance
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Customize how your chat widget looks and behaves
            </Typography>

            {/* Public/Private Toggle */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#fff', borderRadius: 1, border: '1px solid #ff9800' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentCollection?.isPublic || false}
                    onChange={async (e) => {
                      if (!aiId || !collectionId) return;
                      const isPublic = e.target.checked;
                      try {
                        await toggleCollectionPublic(aiId, collectionId, isPublic);
                        // Update local state
                        const res = await getAis();
                        const foundAi = res.data.find((ai: AI) => ai.id === aiId);
                        if (foundAi) {
                          setAi(foundAi);
                        }
                        setSettingsSaved(true);
                        setTimeout(() => setSettingsSaved(false), 3000);
                      } catch (error) {
                        console.error('Error toggling visibility:', error);
                        setError('Failed to update visibility');
                      }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {currentCollection?.isPublic ? '🌐 Public' : '🔒 Private'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {currentCollection?.isPublic
                        ? 'This AI version is publicly accessible via chat widget and demo'
                        : 'This AI version is only visible to you (trainer)'}
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {/* Chatbot Name */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Chatbot Name
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Custom name shown in the chat widget header (leave empty to use AI name)
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={editingChatbotTitle}
                onChange={(e) => setEditingChatbotTitle(e.target.value)}
                placeholder={ai?.title || 'Enter custom name...'}
              />
            </Box>

            {/* Assistant Name */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Assistant Name
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Name displayed next to the AI's avatar in chat messages (leave empty for no name)
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={editingAssistantName}
                onChange={(e) => setEditingAssistantName(e.target.value)}
                placeholder="e.g., Assistant, Helper, AI..."
              />
            </Box>

            {/* Chatbot Icon */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Chatbot Icon
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Custom icon for the chat widget (leave empty for default robot icon)
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {/* Icon Preview */}
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: iconPreview ? 'transparent' : '#2196f3',
                    border: '2px solid #e0e0e0',
                    borderRadius: '50%'
                  }}
                  src={iconPreview || undefined}
                >
                  {!iconPreview && '🤖'}
                </Avatar>

                <Box sx={{ flex: 1 }}>
                  {/* Upload Button */}
                  <label htmlFor="icon-upload">
                    <input
                      id="icon-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleIconUpload}
                      style={{ display: 'none' }}
                    />
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCameraIcon />}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      Upload Icon
                    </Button>
                  </label>

                  {/* Reset Button */}
                  {iconPreview && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<DeleteIcon />}
                      size="small"
                      onClick={handleResetIcon}
                    >
                      Reset to Default
                    </Button>
                  )}
                </Box>
              </Box>

              <Typography variant="caption" color="text.secondary">
                Supported formats: JPG, PNG, GIF. Max size: 2MB
              </Typography>
            </Box>

            {/* Color */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Theme Color
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Primary color for the chat widget interface
              </Typography>
              <TextField
                type="color"
                variant="outlined"
                size="small"
                value={editingColor}
                onChange={(e) => setEditingColor(e.target.value)}
                sx={{ width: '100px' }}
              />
            </Box>

            {/* Highlight Chat */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editingDebugOutline}
                    onChange={(e) => setEditingDebugOutline(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      Highlight Chat
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Show visual outlines around chat elements (for debugging)
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {/* Save Button */}
            <Button
              variant="contained"
              color="warning"
              onClick={handleSaveSettings}
              sx={{ mt: 1 }}
            >
              Save Appearance Settings
            </Button>
          </Paper>

          {/* Curtain Management - Always accessible via button */}
          {showCurtainManagement && (
            <Paper id="curtain-management-section" sx={{ p: 3, mt: 4, bgcolor: '#f3e5f5', border: '2px solid #9c27b0' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#6a1b9a' }}>
                🖼️ Curtain Management
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Upload and manage curtain images for window visualization. These curtains will be used
                for image combination when users upload window photos.
              </Typography>

              <CurtainManagement aiId={aiId || ''} collectionId={collectionId || ''} />
            </Paper>
          )}

          {/* Demo section */}
          <Paper sx={{ p: 3, mt: 4, bgcolor: '#e3f2fd', border: '2px solid #2196f3' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
              🚀 Share AI Demo
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Share this link with others to let them try your AI interactively - no signup or setup required!
            </Typography>
            
            {/* Demo URL field and copy button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                value={demoUrl}
                InputProps={{
                  readOnly: true,
                }}
                size="small"
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyDemo}
              >
                Copy
              </Button>
            </Box>
            
            {/* Launch demo button */}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PlayArrowIcon />}
              onClick={() => navigate(`/demo/${aiId}?collectionId=${collectionId}`)}
              size="large"
            >
              Test Demo
            </Button>
          </Paper>

          {/* Embeddable Widget section */}
          <Paper sx={{ p: 3, mt: 4, bgcolor: '#e8f5e8', border: '2px solid #4caf50' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#2e7d32' }}>
              🌐 Embeddable Widget
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Add this chat widget directly to any website with a simple script tag. No third-party setup required!
            </Typography>
            
            {/* Embed code field and copy button */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                value={embedCode}
                InputProps={{
                  readOnly: true,
                }}
                multiline
                rows={6}
                sx={{ fontFamily: 'monospace' }}
              />
              <Button
                variant="contained"
                color="success"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyEmbed}
                sx={{ minWidth: '100px', alignSelf: 'flex-start' }}
              >
                Copy
              </Button>
            </Box>
            
            <Typography variant="body2" sx={{ mb: 2, color: '#2e7d32' }}>
              <strong>How to use:</strong>
              <ul>
                <li>1. Copy the script tag above</li>
                <li>2. Paste it in the &lt;head&gt; or before closing &lt;/body&gt; tag of your website</li>
                <li>3. The chat widget will appear in the bottom-right corner</li>
                <li>4. All conversations will be logged in your Review dashboard</li>
              </ul>
            </Typography>
            
            <Typography variant="body2" sx={{ mt: 2, p: 2, bgcolor: '#f1f8e9', borderRadius: 1 }}>
              <strong>💡 Tip:</strong> You can customize the widget position by changing <code>data-position</code> to "bottom-left", "top-right", or "top-left".
            </Typography>
          </Paper>
          
          {/* Webhook URL section */}
          <Paper sx={{ p: 2, mt: 4, bgcolor: '#f5f5f5' }}>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                value={webhookUrl}
                InputProps={{
                  readOnly: true,
                }}
                size="small"
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyWebhook}
              >
                Copy
              </Button>
            </Box>

            <br />

            <Typography variant="h6" gutterBottom>
              Crisp Webhook Configuration
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
            <ul>
              <li>1. Copy this webhook URL above.</li>
              <li>2. Go to your Crisp dashboard at https://app.crisp.chat</li>
              <li>3. Navigate to Settings → Plugins → Webhook</li>
              <li>4. Paste the URL and configure it to trigger on message events</li>
              <li>5. Install the Crisp chat widget on your website</li>
              <li>6. Test the integration by sending a message through your Crisp chat</li>
            </ul>
            </Typography>
            
            
            
          </Paper>
        </Box>
      ) : null}
      
      {/* Notification when webhook URL is copied */}
      <Snackbar 
        open={copied} 
        autoHideDuration={3000} 
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Webhook URL copied to clipboard!
        </Alert>
      </Snackbar>

      {/* Notification when demo URL is copied */}
      <Snackbar 
        open={demoCopied} 
        autoHideDuration={3000} 
        onClose={() => setDemoCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Demo URL copied to clipboard!
        </Alert>
      </Snackbar>

      {/* Notification when embed code is copied */}
      <Snackbar
        open={embedCopied}
        autoHideDuration={3000}
        onClose={() => setEmbedCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Embed code copied to clipboard!
        </Alert>
      </Snackbar>

      {/* Notification when settings are saved */}
      <Snackbar
        open={settingsSaved}
        autoHideDuration={3000}
        onClose={() => setSettingsSaved(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Appearance settings saved successfully!
        </Alert>
      </Snackbar>

      {/* Chat reset notification removed */}
    </Box>
  );
};

export default ChatWidget;
